import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { galleryImages as defaultGalleryImages, galleryCategories as defaultGalleryCats } from '@/mocks/galleryData';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { GalleryImage } from '@/mocks/galleryData';

export default function GalleryPage() {
  const { data } = useSiteData();
  const galleryImages = data.galleryImages.length > 0 ? data.galleryImages : defaultGalleryImages;
  const galleryCategories = defaultGalleryCats;
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxImage, setLightboxImage] = useState<GalleryImage | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = useCallback((image: GalleryImage, index: number) => {
    setLightboxImage(image);
    setLightboxIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
  }, []);

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    const newIndex = direction === 'next'
      ? (lightboxIndex + 1) % filteredImages.length
      : (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(filteredImages[newIndex]);
  }, [lightboxIndex, filteredImages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, closeLightbox, navigateLightbox]);

  const getSizeClass = (index: number) => {
    const pattern = index % 6;
    if (pattern === 0 || pattern === 3) return 'row-span-2';
    return '';
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <section className="relative py-24 md:py-36 overflow-hidden">
          {/* Real photo background — collage of 3 photos */}
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/ffdcfd54-4b00-4116-a2e7-12ec73716629_compressed_WhatsApp-Image-2026-07-28-at-1.03.13-PM.webp"
              alt="Happy Faces Hyderabad community"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-foreground-950/95 via-foreground-950/80 to-foreground-950/60"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-accent-500/20 backdrop-blur-sm text-accent-300 text-xs font-semibold rounded-full mb-4 border border-accent-400/30">
              <i className="ri-camera-line mr-1.5"></i>Visual Stories
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Our Gallery of <span className="text-accent-400">Real Impact</span>
            </h1>
            <p className="text-white/65 text-base md:text-lg max-w-2xl mx-auto mb-6">
              Real photographs from Happy Faces Hyderabad events — 29 moments of transformation across Hyderabad &amp; Telangana.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex -space-x-2">
                <img src="https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/03d2b155-bb18-4322-b5ea-e28ef04c548f_compressed_WhatsApp-Image-2026-07-28-at-1.03.10-PM.webp" className="w-8 h-8 rounded-full object-cover border-2 border-white/30" alt="" />
                <img src="https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/3cce0a52-a2a7-4e74-930b-aa1a01e46561_compressed_WhatsApp-Image-2026-07-28-at-1.03.12-PM.webp" className="w-8 h-8 rounded-full object-cover border-2 border-white/30" alt="" />
                <img src="https://storage.readdy-site.link/project_files/a20ac005-7779-424b-9c89-d21aef816197/fcc529ed-7139-48bb-9cb9-20d7d39555df_compressed_WhatsApp-Image-2026-07-28-at-1.03.18-PM.webp" className="w-8 h-8 rounded-full object-cover border-2 border-white/30" alt="" />
              </div>
              <span className="text-white/55 text-sm">29 photos &bull; 8 categories &bull; Hyderabad &amp; Telangana</span>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50 border-b border-background-200/60 sticky top-16 md:top-20 z-30">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {galleryCategories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.name
                      ? 'bg-primary-500 text-white shadow-md'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${activeCategory === cat.name ? 'text-white/70' : 'text-foreground-400'}`}>
                    ({cat.count})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[220px]">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className={`group relative rounded-xl overflow-hidden cursor-pointer ${getSizeClass(index)}`}
                  onClick={() => openLightbox(image, index)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3 md:p-4">
                    <span className="text-accent-300 text-[10px] md:text-xs font-semibold uppercase tracking-wide mb-1">{image.category}</span>
                    <p className="text-white text-xs md:text-sm font-medium leading-snug">{image.alt}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <i className="ri-map-pin-line text-white/50 text-[10px]"></i>
                      <span className="text-white/50 text-[10px]">{image.location}</span>
                    </div>
                  </div>
                  <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-expand-diagonal-line text-white text-xs"></i>
                  </div>
                </div>
              ))}
            </div>

            {filteredImages.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-image-line text-foreground-300 text-2xl"></i>
                </div>
                <p className="text-foreground-400 text-sm">No images in this category yet.</p>
              </div>
            )}
          </div>
        </section>

        {lightboxImage && (
          <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            >
              <i className="ri-arrow-left-line text-white text-lg"></i>
            </button>

            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
            >
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </button>

            <div className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              <img
                src={lightboxImage.src}
                alt={lightboxImage.alt}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-center">
                <span className="text-accent-300 text-xs font-semibold uppercase tracking-wide">{lightboxImage.category}</span>
                <h3 className="text-white text-base md:text-lg font-semibold mt-1">{lightboxImage.alt}</h3>
                <div className="flex items-center justify-center gap-4 mt-2 text-white/50 text-xs">
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{lightboxImage.location}</span>
                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{lightboxImage.date}</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-4">
                {filteredImages.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === lightboxIndex ? 'w-6 bg-accent-400' : 'w-1.5 bg-white/30'}`}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}